/**
 * Pure logic for the EDC Build "Needs review" queue card (correction-loop
 * scope §5): turns verification run records' per-check `correction`
 * annotations into actionable review rows, joined against recorded decline
 * decisions (correctionDecisions).
 *
 * Copy rules apply here because these strings render directly: plain
 * language, no raw check/correction ids as primary labels (the id may appear
 * as small muted text in the card), never "AI".
 *
 * React-free on purpose — exercised by correctionRows.test.ts under the
 * functions package's vitest (`npm run test:web`).
 */

export interface CorrectionPreviewSideLike {
  entries?: Record<string, string>;
  total?: number;
}
export interface CorrectionPreviewLike {
  before?: CorrectionPreviewSideLike;
  after?: CorrectionPreviewSideLike;
}
export interface CorrectionAnnotationLike {
  status: string;
  correctionId: string;
  class?: string;
  preview?: CorrectionPreviewLike;
  deferralReason?: string;
  reason?: string;
  summary?: Record<string, number>;
  /** Further corrections on the same check (the engine's `also[]`).
   * Entries persist their own preview since inc5 step B. */
  also?: Array<{ correctionId: string; status: string; preview?: CorrectionPreviewLike }>;
}
export interface RecordCheckLike {
  checkId: string;
  status?: string;
  correction?: CorrectionAnnotationLike;
  /** Structured per-item findings (S1/S2/S3), as the run record persists them. */
  items?: Array<{ subject: string; detail?: string; kind?: string; fingerprint?: string; data?: Record<string, string> }>;
}
export interface CorrectionDecisionLike {
  correctionId: string;
  reason?: string;
}

export interface ReviewRow {
  family: string;
  correctionId: string;
  /** Plain-language title — never the raw id. */
  title: string;
  /** offered = approvable; failed = an automatic correction that did not verify. */
  kind: 'offered' | 'failed';
  /** "key: was X → now Y" lines from the persisted preview (capped). */
  previewLines: string[];
  /** Truthful item count from the preview; null when the record carries none
   *  (legacy runs predating per-`also[]` previews). */
  count: number | null;
  destructive: boolean;
  declined: boolean;
  declineReason?: string;
  /** Present on failed rows — the honest escalation label. */
  note?: string;
  /** Present on failed rows — the engine's failure reason. */
  failureReason?: string;
  /** The raw persisted preview (when the record carries one) so renderers can
   *  template per-entry customer language (inc5 step F). */
  preview?: CorrectionPreviewLike;
}

/** Plain-language titles per correction id (fallback below, never the raw id). */
const CORRECTION_TITLES: Record<string, string> = {
  'dd-sdtm-mapping-correction':
    'Data dictionary SDTM assignments differ from the mapping',
  'dd-sdtm-notsubmitted-clear':
    'Data dictionary SDTM assignments to clear (mapping marks the fields not submitted)',
};

/** Corrections that delete content (destructive confirm styling). Both v1
 * corrections are field rewrites with recorded before-images, so the set is
 * empty — the plumbing exists for the residue-delete corrections to join. */
const DESTRUCTIVE_CORRECTION_IDS = new Set<string>([]);

export function correctionTitle(correctionId: string): string {
  return CORRECTION_TITLES[correctionId] ?? 'A suggested correction needs review';
}

export function isDestructiveCorrection(correctionId: string): boolean {
  return DESTRUCTIVE_CORRECTION_IDS.has(correctionId);
}

/** "key: was X → now Y" lines from the persisted preview, capped, with the
 * remainder reported from the truthful total (never the capped map size). */
export function buildPreviewLines(
  preview: CorrectionPreviewLike | undefined,
  cap = 8,
): string[] {
  const before = preview?.before?.entries ?? {};
  const after = preview?.after?.entries ?? {};
  const keys = Object.keys(before);
  const lines = keys.slice(0, cap).map((k) => {
    const was = before[k] ?? '—';
    const now = after[k] ?? '—';
    return `${k}: was ${was} → now ${now}`;
  });
  const total = preview?.before?.total ?? keys.length;
  if (total > lines.length) {
    lines.push(`…and ${total - lines.length} more`);
  }
  return lines;
}

function previewCount(preview: CorrectionPreviewLike | undefined): number | null {
  if (!preview?.before) return null;
  if (typeof preview.before.total === 'number') return preview.before.total;
  if (preview.before.entries) return Object.keys(preview.before.entries).length;
  return null;
}

/**
 * Extract the open review rows from one or more record families' checks:
 *  - `offered` corrections (REVIEW class awaiting approval) — primary AND
 *    `also[]` annotations carry their persisted preview (since inc5 step B);
 *  - `failed` corrections (an automatic correction that did not verify),
 *    escalated into the queue with an honest note.
 * Applied / deferred / would_apply corrections are disclosure, not decisions —
 * they render in the per-document verification panel, never here.
 */
/** A customer-actionable item that carries no approvable correction — the
 *  action is guidance (fix the mapping / regenerate the dictionary). */
export interface GuidanceRow {
  kind:
    | 'ambiguous'
    | 'dd-stale'
    // Inc6 step D — customer-facing M/IC classes:
    | 'count-drift'
    | 'form-added'
    | 'form-removed'
    | 'form-renamed'
    | 'schedule-drift'
    | 'stale-name'
    | 'duplicate-mapping-rows'
    | 'codelist-on-result-field'
    | 'codelist-on-specify-other'
    | 'consent-visit-drift'
    | 'consent-stale-visit'
    // Findings stored by the checks that run when a document is generated.
    | 'generation-finding';
  subject: string;
  /** Source check id (dismissals key on checkId + subject + fingerprint). */
  checkId?: string;
  fingerprint?: string;
  data?: Record<string, string>;
}

/** The customer-routed item kinds per check (M2 is builder-side by rule). */
const GUIDANCE_KINDS: Record<string, Set<string>> = {
  M1: new Set(['count-drift', 'form-added', 'form-removed', 'form-renamed', 'schedule-drift']),
  M3: new Set(['stale-name']),
  IC1: new Set(['duplicate-mapping-rows']),
  IC2: new Set(['codelist-on-result-field']),
  IC3: new Set(['codelist-on-specify-other']),
  C1: new Set(['consent-visit-drift']),
  C2: new Set(['consent-stale-visit']), // consent-manifest-missing stays builder-side
};

/**
 * Extract guidance rows from the pipeline run's checks (inc5 step F):
 *  - S3 `ambiguous` items → fix-the-mapping guidance;
 *  - S1 staleness edges INTO the DD → regenerate guidance.
 * Divergence/not-submitted items are handled by the correction offers;
 * mapping-gap items are neither actionable nor gating and stay out.
 */
export function extractPipelineGuidanceRows(checks: RecordCheckLike[]): GuidanceRow[] {
  const rows: GuidanceRow[] = [];
  for (const check of checks ?? []) {
    for (const item of check.items ?? []) {
      const allowed = GUIDANCE_KINDS[check.checkId];
      if (allowed && item.kind && allowed.has(item.kind)) {
        rows.push({
          kind: item.kind as GuidanceRow['kind'],
          subject: item.subject,
          checkId: check.checkId,
          ...(item.fingerprint ? { fingerprint: item.fingerprint } : {}),
          ...(item.data ? { data: item.data } : {}),
        });
        continue;
      }
      if (check.checkId === 'S3' && item.kind === 'ambiguous') {
        rows.push({
          kind: 'ambiguous',
          subject: item.subject,
          ...(item.data ? { data: item.data } : {}),
        });
      } else if (
        check.checkId === 'S1' &&
        (item.subject.startsWith('dataDictionary<-') || item.subject === 'dataDictionary:no-stamp')
      ) {
        rows.push({ kind: 'dd-stale', subject: item.subject });
      }
    }
  }
  return rows;
}

export function extractReviewRows(
  groups: Array<{ family: string; checks: RecordCheckLike[] }>,
  decisions: CorrectionDecisionLike[],
): ReviewRow[] {
  const declineByCorrection = new Map<string, CorrectionDecisionLike>();
  for (const d of decisions) {
    if (d?.correctionId) declineByCorrection.set(d.correctionId, d);
  }

  const rows: ReviewRow[] = [];
  const seen = new Set<string>();

  const push = (
    family: string,
    correctionId: string,
    kind: ReviewRow['kind'],
    preview: CorrectionPreviewLike | undefined,
    failureReason?: string,
  ) => {
    const dedupeKey = `${family}:${correctionId}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    const decision = declineByCorrection.get(correctionId);
    rows.push({
      family,
      correctionId,
      title: correctionTitle(correctionId),
      kind,
      previewLines: buildPreviewLines(preview),
      count: previewCount(preview),
      ...(preview ? { preview } : {}),
      destructive: isDestructiveCorrection(correctionId),
      declined: !!decision,
      ...(decision?.reason ? { declineReason: decision.reason } : {}),
      ...(kind === 'failed'
        ? {
            note: 'Needs review — the automatic correction did not verify',
            ...(failureReason ? { failureReason } : {}),
          }
        : {}),
    });
  };

  for (const group of groups) {
    for (const check of group.checks ?? []) {
      const c = check?.correction;
      if (!c) continue;
      if (c.status === 'offered') {
        push(group.family, c.correctionId, 'offered', c.preview);
      } else if (c.status === 'failed') {
        push(group.family, c.correctionId, 'failed', c.preview, c.reason);
      }
      for (const extra of c.also ?? []) {
        if (extra.status === 'offered') {
          push(group.family, extra.correctionId, 'offered', extra.preview);
        } else if (extra.status === 'failed') {
          push(group.family, extra.correctionId, 'failed', extra.preview);
        }
      }
    }
  }
  return rows;
}
